import React, { useState, useEffect, forwardRef, useMemo, useRef, memo, useCallback } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { fromLonLat } from 'ol/proj';
import isEqual from 'lodash/isEqual';
import { throttle } from 'lodash';
import { IconButton, Menu, MenuItem, Divider, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CircularProgress } from '@mui/material';
import OpenLayersMap from './OpenLayersMap';
import LayerManager from './LayerManager';
import Admin1Layer from './Layers/Admin1Layer';
import CustomLayer from './Layers/CustomLayer';
import TilemapVisualization from '../Tilemap/TilemapVisualization';
import Disclaimer from '../shared/Disclaimer';
import { useMapAnalysis } from '../../hooks/useMapAnalysis';
import { useAdmin1Geometry } from '../../hooks/useAdmin1Geometry';
import { useMapLocations } from '../../hooks/useMapLocations';
import { useMapAnnotations } from '../../hooks/useMapAnnotations';
import { useCustomLayers } from '../../hooks/useCustomLayers';
import { useScheduledAnalysis } from '../../hooks/useScheduledAnalysis';
import { eventBus, EVENTS } from '../../shared/utils/eventBus';
import { 
  Assessment as MethodologyIcon,
  TrendingUp as FindingsIcon,
  Info as LimitationsIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  AutoMode as AutoModeIcon,
  Terminal as TerminalIcon,
  GridOn as GridOnIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import './ol.css';
import styles from './Map.module.css';

// Define TYPE_PRIORITIES constant
const TYPE_PRIORITIES = {
  capital: 1,
  city: 2,
  settlement: 3,
  epicentre: 4,
  default: 99
};

// Helper function to get default colors for severity values
const getDefaultColorForValue = (value) => {
  switch(value) {
    case 1: return '#00FF00'; // Green
    case 2: return '#90EE90'; // Light green
    case 3: return '#FFFF00'; // Yellow
    case 4: return '#CC5500'; // Darker orange
    case 5: return '#990000'; // Dark red
    default: return '#ccc';
  }
};

// Helper function to calculate distance between two points in kilometers using the Haversine formula
const calculateDistance = (lon1, lat1, lon2, lat2) => {
  // Convert degrees to radians
  const toRad = (value) => value * Math.PI / 180;
  
  const R = 6371; // Radius of the Earth in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance;
};

// Helper function to check if a location is near any existing location
const isNearExistingLocation = (lon, lat, existingLocations, threshold = 10) => {
  // Check if the location is within threshold km of any existing location
  for (const existingLocation of Object.values(existingLocations)) {
    const distance = calculateDistance(
      lon, 
      lat, 
      existingLocation.coordinates[0], 
      existingLocation.coordinates[1]
    );
    
    if (distance < threshold) {
      return existingLocation;
    }
  }
  
  return null;
};

// Helper function to get a normalized key for a location
const getLocationCacheKey = (name, lon, lat) => {
  // Normalize the name (lowercase, remove special characters)
  const normalizedName = name ? name.toLowerCase().replace(/[^\w\s]/g, '') : '';
  
  // Round coordinates to reduce precision (helps with matching)
  const roundedLon = lon ? parseFloat(lon).toFixed(2) : '';
  const roundedLat = lat ? parseFloat(lat).toFixed(2) : '';
  
  // Return a combination of name and coordinates as the key
  if (normalizedName && roundedLon && roundedLat) {
    return `${normalizedName}|${roundedLon},${roundedLat}`;
  } else if (normalizedName) {
    return `name:${normalizedName}`;
  } else if (roundedLon && roundedLat) {
    return `coord:${roundedLon},${roundedLat}`;
  }
  
  return null;
};

// Helper function to calculate string similarity (Levenshtein distance ratio)
const calculateStringSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  
  // Convert to lowercase for case-insensitive comparison
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  // Calculate Levenshtein distance
  const track = Array(s2.length + 1).fill(null).map(() => 
    Array(s1.length + 1).fill(null));
  
  for (let i = 0; i <= s1.length; i += 1) {
    track[0][i] = i;
  }
  
  for (let j = 0; j <= s2.length; j += 1) {
    track[j][0] = j;
  }
  
  for (let j = 1; j <= s2.length; j += 1) {
    for (let i = 1; i <= s1.length; i += 1) {
      const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator, // substitution
      );
    }
  }
  
  // Calculate similarity ratio (0 to 1, where 1 is perfect match)
  const distance = track[s2.length][s1.length];
  const maxLength = Math.max(s1.length, s2.length);
  const similarity = 1 - distance / maxLength;
  
  return similarity;
};

// Helper function to check if two strings are similar
const areSimilarNames = (name1, name2, threshold = 0.8) => {
  if (!name1 || !name2) return false;
  
  // Normalize names - convert to lowercase, remove spaces, and special characters
  const normalizedName1 = name1.toLowerCase().replace(/[^\w]/g, '');
  const normalizedName2 = name2.toLowerCase().replace(/[^\w]/g, '');
  
  // Special handling for airports - they should not match with cities
  const isAirport1 = name1.toLowerCase().includes('airport');
  const isAirport2 = name2.toLowerCase().includes('airport');
  
  if (isAirport1 !== isAirport2) {
    return false; // One is an airport, one is not - don't match
  }
  
  // Special handling for common transliteration variations
  // Strip spaces and compare - this helps with cases like "Nay Pyi Taw" vs "Naypyidaw"
  if (normalizedName1 === normalizedName2) {
    return true; // Exact match after normalization
  }
  
  // Try transliteration replacements for common cases
  // These are common replacements in transliterated place names
  const transliterations = [
    { from: 'ph', to: 'f' },
    { from: 'yi', to: 'i' },
    { from: 'y', to: 'i' },
    { from: 'oo', to: 'u' },
    { from: 'ee', to: 'i' },
    { from: 'ow', to: 'au' },
    { from: 'c', to: 'k' },
    { from: 'q', to: 'k' }
  ];
  
  let transformedName1 = normalizedName1;
  let transformedName2 = normalizedName2;
  
  transliterations.forEach(({ from, to }) => {
    transformedName1 = transformedName1.replace(new RegExp(from, 'g'), to);
    transformedName2 = transformedName2.replace(new RegExp(from, 'g'), to);
  });
  
  if (transformedName1 === transformedName2) {
    return true; // Match after transliteration normalization
  }
  
  // Don't match if one name is much longer than the other (likely different entities)
  const lengthRatio = Math.min(normalizedName1.length, normalizedName2.length) / 
                      Math.max(normalizedName1.length, normalizedName2.length);
  if (lengthRatio < 0.6) {
    return false; // One name is much longer than the other
  }
  
  // Finally, fall back to Levenshtein distance
  const similarityScore = calculateStringSimilarity(normalizedName1, normalizedName2);
  
  // Use a higher threshold for longer names to prevent false matches
  const dynamicThreshold = Math.min(threshold + (Math.max(normalizedName1.length, normalizedName2.length) / 100), 0.9);
  
  return similarityScore >= dynamicThreshold;
};

// Helper function to check if location types should be considered compatible
const areCompatibleTypes = (type1, type2) => {
  if (!type1 || !type2) return true; // If either type is missing, consider them compatible
  
  type1 = type1.toLowerCase();
  type2 = type2.toLowerCase();
  
  // Exact match is always compatible
  if (type1 === type2) return true;
  
  // Define type groups that are compatible with each other
  const cityTypes = ['city', 'town', 'settlement', 'village', 'capital'];
  const borderTypes = ['border', 'border_crossing', 'bordercrossing', 'bordercrossingactive', 'bordercrossingpotential'];
  const campTypes = ['camp', 'refugee_camp', 'idp_camp', 'idp-refugee-camp'];
  const locationTypes = ['location', 'region']; // Group location and region types together
  
  // Check if both types are in the same group
  if (cityTypes.includes(type1) && cityTypes.includes(type2)) return true;
  if (borderTypes.includes(type1) && borderTypes.includes(type2)) return true;
  if (campTypes.includes(type1) && campTypes.includes(type2)) return true;
  if (locationTypes.includes(type1) && locationTypes.includes(type2)) return true;
  
  // Special case: 'epicentre' type should only match with itself
  if (type1 === 'epicentre' || type2 === 'epicentre') return type1 === type2;
  
  // Special case: 'airport' should only match with airport-related types
  if (type1.includes('airport') || type2.includes('airport')) {
    return type1.includes('airport') && type2.includes('airport');
  }
  
  // Default to not compatible if no rules matched
  return false;
};

const getCanonicalLocation = (location) => {
  if (!location || (!location.name && (!location.lon || !location.lat))) {
    return null;
  }
  
  // For locations with epicentre type, standardize the name
  let name = location.name;
  const lon = parseFloat(location.lon);
  const lat = parseFloat(location.lat);
  const type = location.type?.toLowerCase() || '';
  
  // Standardize epicentre names
  if (type === 'epicentre') {
    name = 'Epicentre';
  }
  
  // Create a new canonical location
  const canonical = {
    name: name, // Use the standardized name (which might be 'Epicentre' for epicentres)
    coordinates: [lon, lat],
    type: type || 'city'
  };
  
  return canonical;
};

// Helper function to convert hex color to rgba with opacity
const hexToRgba = (hex, opacity) => {
  // Default fallback color with opacity
  if (!hex || typeof hex !== 'string') return `rgba(204, 204, 204, ${opacity})`;
  
  // Check if it's already an rgba color
  if (hex.startsWith('rgba')) return hex;
  if (hex.startsWith('rgb')) {
    // Convert rgb to rgba
    return hex.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }
  
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Handle shorthand hex (e.g., #FFF)
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  // Convert hex to rgb
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Helper function to remove source numbers like [3][4] from text
const removeSourceNumbers = (text) => {
  if (!text) return '';
  return text.replace(/\[\d+\]/g, '').trim();
};

// Helper function to check if coordinates are within buffer zone
const isWithinBufferZone = (newCoords, existingCoords, bufferKm = 100) => {
  if (!existingCoords) return false;
  const distance = calculateDistance(
    newCoords[0],
    newCoords[1],
    existingCoords[0],
    existingCoords[1]
  );
  return distance <= bufferKm;
};

// Memoize the legend item component
const LegendItem = memo(({ item, hoveredSeverity, setHoveredSeverity }) => {
  const severityValue = useMemo(() => 
    item.id !== undefined ? item.id : 
    (item.value !== undefined ? item.value : item.title),
    [item.id, item.value, item.title]
  );
  
  const isHovered = useMemo(() => 
    typeof severityValue === 'string' ? 
      hoveredSeverity === severityValue : 
      hoveredSeverity === Number(severityValue),
    [severityValue, hoveredSeverity]
  );
    
  const categoryTitle = useMemo(() => 
    item.title || 
    (item.description ? item.description.split(':')[0].trim() : 
    (typeof severityValue === 'string' ? severityValue : `${severityValue}`)),
    [item.title, item.description, severityValue]
  );

  const handleMouseEnter = useCallback(() => {
    let hoverValue;
    if (typeof severityValue === 'string' && !isNaN(Number(severityValue))) {
      hoverValue = Number(severityValue);
    } else {
      hoverValue = severityValue;
    }
    setHoveredSeverity(hoverValue);
  }, [severityValue, setHoveredSeverity]);

  const handleMouseLeave = useCallback(() => {
    setHoveredSeverity(null);
  }, [setHoveredSeverity]);

  return (
    <div 
      style={{ 
        display: 'flex', 
        flexDirection: 'column',
        padding: '0 0 0 0',
      }}
    >
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          width: 'fit-content',
          padding: '4px 0'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ 
          width: '10px', 
          height: '10px', 
          backgroundColor: item.color ? 
            hexToRgba(item.color, isHovered ? 0.30333 : 0.222) : 
            hexToRgba(getDefaultColorForValue(severityValue), isHovered ? 0.30333 : 0.222),
          borderRadius: '1px',
          marginRight: '5px',
          flexShrink: 0,
          transition: 'background-color 0.2s ease'
        }}></div>
        <span style={{ 
          fontSize: 'var(--font-size-md)', 
          lineHeight: 'var(--line-height-tight)',
          fontWeight: isHovered ? 'bold' : 'normal'
        }}>
          {categoryTitle}
        </span>
      </div>
    </div>
  );
});

LegendItem.displayName = 'LegendItem';

// Memoize the legend tooltip component
const LegendTooltip = memo(({ hoveredSeverity, mapLegend }) => {
  if (hoveredSeverity === null) return null;

  const getCategoryTitle = useCallback(() => {
    if (Array.isArray(mapLegend)) {
      const item = mapLegend.find(item => {
        let severityValue;
        if (item.title === 'Basic Shelter' || 
            item.title === 'Enhanced Shelter' || 
            item.title === 'Bridge/Transitional Housing' || 
            item.title === 'Sanctioned Encampments') {
          severityValue = item.title;
        } else {
          severityValue = item.id !== undefined ? item.id : 
                         (item.value !== undefined ? item.value : item.title);
        }
        return typeof hoveredSeverity === 'string' ? 
          String(severityValue) === hoveredSeverity : 
          Number(severityValue) === hoveredSeverity;
      });
      return item?.label || item?.title || (item?.description?.split(':')[0]) || `${hoveredSeverity}`;
    }
    return `${hoveredSeverity}`;
  }, [mapLegend, hoveredSeverity]);

  const getDescription = useCallback(() => {
    if (Array.isArray(mapLegend)) {
      const item = mapLegend.find(item => {
        const itemValue = item.title !== undefined && typeof hoveredSeverity === 'string' ? 
          item.title : (item.id !== undefined ? item.id : item.value);
        return typeof hoveredSeverity === 'string' ? 
          String(itemValue) === hoveredSeverity : 
          Number(itemValue) === hoveredSeverity;
      });
      return removeSourceNumbers(item?.description || '');
    }
    return '';
  }, [mapLegend, hoveredSeverity]);

  return (
    <div 
      key={`legend-hover-${hoveredSeverity}`}
      style={{
        fontSize: 'var(--font-size-md)',
        lineHeight: 'var(--line-height-normal)',
        marginTop: '8px',
        marginLeft: '-10px',
        marginRight: '-10px',
        padding: '8px',
        color: 'var(--color-fg-default)',
        borderRadius: '4px',
        maxWidth: '300px',
        width: '300px',
        position: 'relative',
        left: '10px',
      }}>
      <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: 'var(--font-size-md)' }}>
        {getCategoryTitle()}
      </div>
      <div style={{ marginTop: '2px', fontStyle: 'italic' }}>
        {getDescription()}
      </div>
    </div>
  );
});

LegendTooltip.displayName = 'LegendTooltip';

const Map = memo(forwardRef(({
  view,
  mapOptions: initialMapOptions,
  layers,
  showLegend = false,
  mapSettings,
  isLoading,
  error,
  selectedPillarKey,
  selectedSubPillar = null,
  onMapAnalysisData,
  onUpdateLayers,
  eventsPanelCollapsed = false,
  paddingBottom = 20,
  selectedPillarId,
  showHeader = true,
  selectedView,
  adm1Data,
  fontVersion = 0,
}, ref) => {
  // Add refs for position elements
  const bottomRightRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const topLeftRef = useRef(null);
  
  // Add a ref to store the hideTooltip function from Admin1Layer
  const hideTooltipRef = useRef(null);
  const admin1LayerRef = useRef(null);
  
  // Throttled layer update function to prevent excessive re-renders
  const throttledUpdateLayers = useCallback(
    throttle((updatedLayers) => {
      if (typeof onUpdateLayers === 'function') {
        onUpdateLayers(prevLayers => {
          // Only update if the layers actually changed by comparing their IDs
          // to prevent unnecessary re-renders in the parent
          const prevIds = (prevLayers || []).map(l => l.id).sort().join(',');
          const newIds = (updatedLayers || []).map(l => l.id).sort().join(',');
          if (prevIds === newIds) return prevLayers;
          return updatedLayers;
        });
      }
    }, 150), // 150ms throttle
    [onUpdateLayers]
  );

  useEffect(() => {
    console.log('[Map.jsx] Mounted!');
    return () => {
      console.log('[Map.jsx] Unmounted!');
    };
  }, []);

  // Track pillar key, sub-pillar, and view changes
  useEffect(() => {
    // Effect runs when these dependencies change
  }, [selectedPillarKey, selectedSubPillar, view]);
  
  // Fetch admin1 geometry for the selected view
  const { data: admin1GeometryData, loading: admin1GeometryLoading, error: admin1GeometryError } = 
    useAdmin1Geometry(view?.iso3);
    
  // Track admin1 geometry data changes
  useEffect(() => {
    // Effect runs when admin1 geometry data changes
  }, [admin1GeometryData, admin1GeometryLoading, admin1GeometryError]);
  const [map, setMap] = useState(null);
  const [mapOptions, setMapOptions] = useState(initialMapOptions);
  
  // State for tilemap availability
  const [tilemapAvailable, setTilemapAvailable] = useState(false);
  
  // State to store the view ID after fetching it from ISO3
  const [viewId, setViewId] = useState(null);
  const lastValidViewInfoRef = useRef({ iso3: null, id: null }); // Ref to store last valid view info
  
  // State for map title, description and legend from analysis data
  const [mapTitle, setMapTitle] = useState('');
  const [mapDescription, setMapDescription] = useState('');
  const [mapLegend, setMapLegend] = useState(null);
  const [hoveredSeverity, setHoveredSeverity] = useState(null);
  const [hoveredPcode, setHoveredPcode] = useState(null);
  const [isMouseOverLegend, setIsMouseOverLegend] = useState(false);
  const [displayableMapInfo, setDisplayableMapInfo] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    methodology: false,
    findings: false,
    limitations: false
  });
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const mapAnalysisPollTimerRef = useRef(null);
  const mapAnalysisPollStopAtRef = useRef(0);
  const currentMapAnalysisIdRef = useRef(null);
  const mapAnalysisPollPreviousIdRef = useRef(null);
  
  // Function to toggle expanded sections
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);
  
  // Function to handle mouse leave from map container
  const handleMapContainerMouseLeave = useCallback(() => {
    // Hide Admin1Layer tooltip if available
    if (hideTooltipRef.current) {
      hideTooltipRef.current();
    }
    
    // Reset hovered severity to hide legend tooltip
    setHoveredSeverity(null);
    
    // Hide any DTM tooltips that might be present
    const dtmTooltip = document.querySelector('.dtm-tooltip');
    if (dtmTooltip) {
      dtmTooltip.style.display = 'none';
      dtmTooltip.style.visibility = 'hidden';
    }
    
    // Hide any other tooltips that might be present
    const tooltips = document.querySelectorAll('.map-tooltip');
    tooltips.forEach(tooltip => {
      tooltip.style.display = 'none';
      tooltip.style.visibility = 'hidden';
    });
  }, []);
  
  // Debug logging for hoveredSeverity changes
  // Track changes to hovered severity
  useEffect(() => {
    // Effect runs when hoveredSeverity changes
  }, [hoveredSeverity]);
  
  // Track changes to map legend
  useEffect(() => {
    // Effect runs when mapLegend changes
  }, [mapLegend]);
  
  // Check if tilemap is available for this view
  useEffect(() => {
    const checkTilemapAvailable = async () => {
      if (!viewId) {
        setTilemapAvailable(false);
        return;
      }
      
      try {
        const response = await fetch(`/api/tilemap/${viewId}/data`);
        if (response.ok) {
          const data = await response.json();
          const available = data.enabled && data.tiles && data.tiles.length > 0;
          setTilemapAvailable(available);
        } else {
          setTilemapAvailable(false);
        }
      } catch (error) {
        console.error('Error checking tilemap availability:', error);
        setTilemapAvailable(false);
      }
    };
    
    checkTilemapAvailable();
  }, [viewId]);
  
  // Apply horizontal offset using view padding
  useEffect(() => {
    if (map) {
      const view = map.getView();
      // Padding: [top, right, bottom, left]
      view.padding = [0, 0, 0, 50]; 
      // Force a re-render to apply the padding visually immediately
      map.render(); 
    }
  }, [map]); // Run when the map object is available
  
  // Fetch view ID from ISO3 code
  useEffect(() => {
    const fetchViewId = async () => {
      if (view?.iso3) {
        try {
          const response = await fetch(`/api/views?iso3=${view.iso3}`);
          if (!response.ok) {
            setViewId(null);
            throw new Error('Failed to fetch view data');
          }
          const data = await response.json();
          if (data && data.length > 0) {
            setViewId(data[0].id);
            lastValidViewInfoRef.current = { iso3: view.iso3, id: data[0].id }; // Update ref
          } else {
            setViewId(null);
          }
        } catch (error) {
          setViewId(null);
        }
      } else {
        setViewId(null);
      }
    };
    
    fetchViewId();
  }, [view?.iso3]);
  
  // Use sub-pillar ID if present, otherwise parent pillar ID
  const derivedSelectedSubPillar = useMemo(() => {
    if (selectedSubPillar?.id) {
      return selectedSubPillar;
    }

    if (!selectedPillarId || !selectedView?.pillars || !Array.isArray(selectedView.pillars)) {
      return null;
    }

    const selectedParentPillar = selectedView.pillars.find((pillar) => pillar.id === selectedPillarId);
    if (!selectedParentPillar || selectedParentPillar.is_visible !== false) {
      return null;
    }

    const firstVisibleChild = selectedView.pillars
      .filter((pillar) => pillar.parent_pillar_id === selectedParentPillar.id && pillar.is_visible === true)
      .sort((a, b) => {
        const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
        const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
        if (aCustom !== bCustom) return aCustom - bCustom;
        const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
        const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      })[0] || null;

    return firstVisibleChild;
  }, [selectedPillarId, selectedSubPillar, selectedView?.pillars]);

  const effectiveSelectedSubPillar = selectedSubPillar || derivedSelectedSubPillar;
  const effectivePillarId = effectiveSelectedSubPillar?.id || selectedPillarId;
  
  // Add a render counter to reduce logging frequency
  const mapRenderCountRef = useRef(0);
  
  // Determine the viewId to use for the hook, preferring current state,
  // but falling back to last valid if current prop matches last valid ISO3.
  let hookViewId = viewId;
  if (hookViewId === null && view?.iso3 && view.iso3 === lastValidViewInfoRef.current.iso3) {
    hookViewId = lastValidViewInfoRef.current.id;
  }

  const { isProcessing: isSchedulerProcessing } = useScheduledAnalysis(hookViewId, effectivePillarId, { taskTypes: ['map_analysis'], enableScheduleFetch: false });

  // Increment render counter
  mapRenderCountRef.current += 1;

  const {
    mapAnalysisData,
    isLoading: mapAnalysisLoading,
    error: mapAnalysisError,
    refetch: refetchMapAnalysis,
    loadMapAnalysisById
  } = useMapAnalysis(
    hookViewId, // Use the potentially stabilized viewId
    effectivePillarId
  );

  // Listen for batch task completion to refresh map analysis data
  const handleBatchTaskCompleted = useCallback((eventPayload) => {
    if (eventPayload && eventPayload.dataType === 'map_analysis') {
      const taskViewId = eventPayload.viewId ? String(eventPayload.viewId) : null;
      const taskPillarId = eventPayload.pillarId ? String(eventPayload.pillarId) :
                           (eventPayload.taskDetails?.pillar_id ? String(eventPayload.taskDetails.pillar_id) : null);

      const currentViewIdStr = viewId ? String(viewId) : null;
      const currentPillarIdStr = effectivePillarId ? String(effectivePillarId) : null;

      if (taskViewId && currentViewIdStr && taskViewId === currentViewIdStr &&
          taskPillarId && currentPillarIdStr && taskPillarId === currentPillarIdStr) {
        if (refetchMapAnalysis) {
          refetchMapAnalysis();
        }
      }
    }
  }, [viewId, effectivePillarId, refetchMapAnalysis]);

  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENTS.BATCH_TASK_COMPLETED, handleBatchTaskCompleted);
    return () => {
      unsubscribe();
    };
  }, [handleBatchTaskCompleted]);
  
  // Process map analysis data when it changes
  useEffect(() => {
    if (view?.iso3 && effectivePillarId) {
      if (mapAnalysisData) {
        const effectiveAt = mapAnalysisData.completed_at || mapAnalysisData.updated_at || mapAnalysisData.created_at;
        if (mapAnalysisData.content) {
          try {
            const parsedData = JSON.parse(mapAnalysisData.content);
            if (parsedData.title && parsedData.legend) {
              setDisplayableMapInfo({
                title: parsedData.title,
                description: parsedData.description || '',
                legend: parsedData.legend,
                updatedAt: effectiveAt,
                methodology: parsedData.methodology || '',
                mainFindings: parsedData.main_findings || '',
                limitations: parsedData.limitations || '',
                data: parsedData.data || []
              });
              setMapTitle(parsedData.title);
              setMapDescription(parsedData.description || '');
              setMapLegend(parsedData.legend);
              if (effectiveAt) {
                setMapUpdatedAt(new Date(effectiveAt));
              }
              setParsedMapContent(parsedData); 
            } else {
              setDisplayableMapInfo(null);
              setParsedMapContent(null);
              setMapTitle('');
              setMapDescription('');
              setMapLegend(null);
            }
          } catch (e) {
            setDisplayableMapInfo(null);
            setParsedMapContent(null);
            setMapTitle('');
            setMapDescription('');
            setMapLegend(null);
          }
        } else {
          setDisplayableMapInfo(null);
          setParsedMapContent(null);
          setMapTitle('');
          setMapDescription('');
          setMapLegend(null);
        }
      } else {
        setDisplayableMapInfo(null);
        setParsedMapContent(null);
        setMapTitle('');
        setMapDescription('');
        setMapLegend(null);
      }
    }
  }, [view?.iso3, effectivePillarId, mapAnalysisData]); // mapAnalysisData is the primary trigger

  // Memoize the initial center calculation
  const initialCenter = useMemo(() => 
    fromLonLat(initialMapOptions?.center || [0, 0]),
    [initialMapOptions?.center]
  );

  useEffect(() => {
    // Map ref tracking
  }, [ref]);
  
  // State to store parsed map analysis content
  const [parsedMapContent, setParsedMapContent] = useState(null);
  const [mapUpdatedAt, setMapUpdatedAt] = useState(null);

  // Format the last updated time in a human-readable way with dynamic updates
  const [formattedLastUpdated, setFormattedLastUpdated] = useState(null);
  const dateFieldRef = useRef(null);

  const suppressLegendHoverUntilRef = useRef(0);

  const [mapAnalysisHistory, setMapAnalysisHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mapAnalysisToDelete, setMapAnalysisToDelete] = useState(null);
  
  // Auth context for super admin features
  const { user } = useAuth();
  const isSuperAdmin = user?.is_admin || user?.role === 'super_admin' || user?.organization_role === 'super_admin';

  useEffect(() => {
    currentMapAnalysisIdRef.current = mapAnalysisData?.id ?? null;
  }, [mapAnalysisData?.id]);

  const stopMapAnalysisPolling = useCallback(() => {
    if (mapAnalysisPollTimerRef.current) {
      window.clearInterval(mapAnalysisPollTimerRef.current);
      mapAnalysisPollTimerRef.current = null;
    }
    mapAnalysisPollStopAtRef.current = 0;
    mapAnalysisPollPreviousIdRef.current = null;
  }, []);

  const startMapAnalysisPollingForNewRecord = useCallback(() => {
    stopMapAnalysisPolling();

    if (!hookViewId || !effectivePillarId) {
      throw new Error('Missing view or pillar context for map analysis polling');
    }

    mapAnalysisPollPreviousIdRef.current = currentMapAnalysisIdRef.current;
    mapAnalysisPollStopAtRef.current = Date.now() + 2 * 60 * 1000;

    mapAnalysisPollTimerRef.current = window.setInterval(async () => {
      try {
        if (Date.now() > mapAnalysisPollStopAtRef.current) {
          stopMapAnalysisPolling();
          throw new Error('Timed out waiting for new map analysis to appear');
        }

        const response = await fetch(`/api/map-analysis/pillar-history/${effectivePillarId}/${hookViewId}`, {
          credentials: 'include'
        });
        const result = await response.json();
        if (!response.ok || !result?.success || !Array.isArray(result.data)) {
          throw new Error(result?.error || 'Failed to fetch map analysis history while polling');
        }

        const newest = result.data[0];
        const previousId = mapAnalysisPollPreviousIdRef.current;

        if (newest?.id && newest.id !== previousId) {
          setMapAnalysisHistory(result.data);
          setCurrentHistoryIndex(0);
          if (loadMapAnalysisById) {
            loadMapAnalysisById(newest.id);
          }
          stopMapAnalysisPolling();
        }
      } catch (err) {
        console.error('Map analysis polling failed:', err);
        stopMapAnalysisPolling();
        alert(`Map analysis refresh failed: ${err?.message || err}`);
      }
    }, 2000);
  }, [stopMapAnalysisPolling, hookViewId, effectivePillarId, loadMapAnalysisById]);

  useEffect(() => {
    return () => {
      stopMapAnalysisPolling();
    };
  }, [stopMapAnalysisPolling]);

  // Update the formatted date initially and set up the timer
  useEffect(() => {
    // Use mapAnalysisData.updated_at
    const dateField = displayableMapInfo?.updatedAt || mapAnalysisData?.completed_at || mapAnalysisData?.updated_at || mapAnalysisData?.created_at;
    dateFieldRef.current = dateField;

    if (!dateField) {
      setFormattedLastUpdated(null);
      return;
    }

    // Function to update the formatted date
    const updateFormattedDate = () => {
      try {
        const updatedDate = new Date(dateFieldRef.current);
        const formatted = formatDistanceToNow(updatedDate, { addSuffix: true });
        setFormattedLastUpdated(formatted.replace('about ', ''));
      } catch (e) {
        setFormattedLastUpdated(null);
      }
    };

    // Initial update
    updateFormattedDate();

    // Set up interval to update every minute (60000ms)
    const intervalId = setInterval(updateFormattedDate, 60000);

    return () => clearInterval(intervalId);
  }, [displayableMapInfo?.updatedAt, mapAnalysisData?.completed_at, mapAnalysisData?.updated_at, mapAnalysisData?.created_at]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!hookViewId || !effectivePillarId) return;

      setLoadingHistory(true);
      try {
        const response = await fetch(`/api/map-analysis/pillar-history/${effectivePillarId}/${hookViewId}`);
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setMapAnalysisHistory(result.data);
          const currentIndex = result.data.findIndex((item) => item.id === mapAnalysisData?.id);
          setCurrentHistoryIndex(currentIndex >= 0 ? currentIndex : 0);
        } else {
          setMapAnalysisHistory([]);
          setCurrentHistoryIndex(0);
        }
      } catch (err) {
        console.error('Error fetching map analysis history:', err);
        setMapAnalysisHistory([]);
        setCurrentHistoryIndex(0);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [hookViewId, effectivePillarId, mapAnalysisData?.id]);

  const handlePreviousMapAnalysis = useCallback(() => {
    if (currentHistoryIndex < mapAnalysisHistory.length - 1) {
      const nextIndex = currentHistoryIndex + 1;
      const nextAnalysis = mapAnalysisHistory[nextIndex];
      if (loadMapAnalysisById && nextAnalysis?.id) {
        loadMapAnalysisById(nextAnalysis.id);
      }
    }
  }, [currentHistoryIndex, mapAnalysisHistory, loadMapAnalysisById]);

  const handleNextMapAnalysis = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const prevIndex = currentHistoryIndex - 1;
      const prevAnalysis = mapAnalysisHistory[prevIndex];
      if (loadMapAnalysisById && prevAnalysis?.id) {
        loadMapAnalysisById(prevAnalysis.id);
      }
    }
  }, [currentHistoryIndex, mapAnalysisHistory, loadMapAnalysisById]);

  const handleHistoryMenuOpen = useCallback((event) => {
    if (mapAnalysisHistory.length <= 1 && !isSuperAdmin) return;
    if (event?.stopPropagation) event.stopPropagation();
    if (event?.preventDefault) event.preventDefault();
    setHistoryMenuAnchor(event.currentTarget);
  }, [mapAnalysisHistory.length, isSuperAdmin]);

  const handleHistoryMenuClose = useCallback(() => {
    setHistoryMenuAnchor(null);
    suppressLegendHoverUntilRef.current = Date.now() + 600;
    setIsMouseOverLegend(false);
  }, []);

  const handleSelectHistoryItem = useCallback((mapAnalysisId) => {
    if (loadMapAnalysisById && mapAnalysisId) {
      loadMapAnalysisById(mapAnalysisId);
      const selectedIndex = mapAnalysisHistory.findIndex(item => item.id === mapAnalysisId);
      if (selectedIndex >= 0) {
        setCurrentHistoryIndex(selectedIndex);
      }
    }
    handleHistoryMenuClose();
    suppressLegendHoverUntilRef.current = Date.now() + 300;
  }, [loadMapAnalysisById, mapAnalysisHistory, handleHistoryMenuClose]);

  const handleViewMapAnalysisInAdmin = useCallback((e, mapAnalysisId) => {
    e.stopPropagation();
    const adminUrl = import.meta.env.PROD ? '/admin/' : 'http://localhost:3000/admin/';
    window.open(`${adminUrl}map-analysis/${mapAnalysisId}`, '_blank');
  }, []);

  const handleDeleteMapAnalysisClick = useCallback((e, mapAnalysisId) => {
    e.stopPropagation();
    setMapAnalysisToDelete(mapAnalysisId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteMapAnalysisConfirm = useCallback(async () => {
    if (!mapAnalysisToDelete) return;
    
    // Check if we are deleting the currently viewed analysis
    const isDeletingCurrent = mapAnalysisToDelete === mapAnalysisData?.id;

    try {
      await fetch(`/api/map-analysis/${mapAnalysisToDelete}`, { method: 'DELETE', credentials: 'include' });
      setDeleteDialogOpen(false);
      setMapAnalysisToDelete(null);
      handleHistoryMenuClose();
      // Refresh history list
      const response = await fetch(`/api/map-analysis/pillar-history/${effectivePillarId}/${hookViewId}`);
      const result = await response.json();
      if (result?.success && Array.isArray(result.data)) {
        setMapAnalysisHistory(result.data);

        // If we deleted the current analysis, switch to the most recent one (first in the list)
        if (isDeletingCurrent && result.data.length > 0 && loadMapAnalysisById) {
          loadMapAnalysisById(result.data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to delete map analysis:', err);
      alert('Failed to delete map analysis record');
    }
  }, [mapAnalysisToDelete, handleHistoryMenuClose, effectivePillarId, hookViewId, mapAnalysisData?.id, loadMapAnalysisById]);

  const handleDeleteMapAnalysisCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setMapAnalysisToDelete(null);
  }, []);

  const handleRegenerateMapAnalysis = useCallback((e) => {
    e.stopPropagation();
    setRegenerateDialogOpen(true);
  }, []);

  const handleRegenerateConfirm = useCallback(async () => {
    setRegenerateDialogOpen(false);
    
    try {
      const viewId = hookViewId;
      const pillarId = effectivePillarId;
      if (!viewId || !pillarId) {
        throw new Error('Missing view or pillar context for regeneration');
      }

      const saveResponse = await fetch('/api/saved-batch-configs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: `Temp map analysis regenerate (view ${viewId}, pillar ${pillarId})`,
          views: [viewId],
          pillars: [pillarId],
          types: ['map_analysis'],
          scheduleEnabled: false,
          scheduleIntervalValue: 1,
          scheduleIntervalUnit: 'days',
          schedule: null,
          starred: false,
          temporary: true
        })
      });

      const saveResult = await saveResponse.json();
      if (!saveResponse.ok || !saveResult?.success) {
        throw new Error(saveResult?.error || 'Failed to create temporary batch configuration');
      }

      const configId = saveResult?.data?.id;
      if (!configId) {
        throw new Error('Temporary batch configuration created without an ID');
      }

      const runResponse = await fetch(`/api/scheduler/run-now/${configId}` , {
        method: 'POST',
        credentials: 'include'
      });

      const runResult = await runResponse.json();
      if (!runResponse.ok || !runResult?.success) {
        throw new Error(runResult?.error || 'Failed to start scheduled regeneration');
      }

      startMapAnalysisPollingForNewRecord();

      handleHistoryMenuClose();
    } catch (err) {
      console.error('Failed to regenerate map analysis:', err);
      alert('Failed to start map analysis regeneration: ' + err.message);
    }
  }, [hookViewId, effectivePillarId, handleHistoryMenuClose, startMapAnalysisPollingForNewRecord]);

  const handleRegenerateCancel = useCallback(() => {
    setRegenerateDialogOpen(false);
  }, []);

  const handleEditPillar = useCallback((e) => {
    e.stopPropagation();
    const adminUrl = import.meta.env.PROD ? '/admin/' : 'http://localhost:3000/admin/';
    // Use sub-pillar ID if available, otherwise use parent pillar ID
    const pillarIdToEdit = effectiveSelectedSubPillar?.id || selectedPillarId;
    window.open(`${adminUrl}pillars?pillarId=${pillarIdToEdit}&promptTab=map`, '_blank');
  }, [effectiveSelectedSubPillar, selectedPillarId]);

  const handleLegendMouseEnter = useCallback(() => {
    if (Date.now() < suppressLegendHoverUntilRef.current) {
      return;
    }
    setIsMouseOverLegend(true);
  }, []);

  // Replace the old location processing with the new hook
  const { locations, isLoading: locationsLoading, error: locationsError } = useMapLocations(
    view?.iso3,
    mapAnalysisData
  );

  // Fetch custom layers for the current view
  const { layers: customLayers, isLoading: customLayersLoading } = useCustomLayers(
    viewId,
    { isVisible: true }
  );

  // Memoize the layer creation logic
  const createSymbolLayer = useCallback((type, features) => {
        let symbolType = 'city';
        let zIndexValue = 16;
        let scaleValue = 0.7;
        let showLabels = true;
    let declutterPriority = 0; // Base declutter priority for symbols
        
        switch(type) {
          case 'capital':
            symbolType = 'capital';
            zIndexValue = 19;
            scaleValue = 0.83;
        declutterPriority = 0.9; // Highest priority for capitals
            break;
      case 'epicentre':
        symbolType = 'epicentre';
        zIndexValue = 16;
        scaleValue = 1.0;
        showLabels = false;
        declutterPriority = 0.8; // High priority for epicentres
            break;
          case 'settlement':
            symbolType = 'settlement';
            zIndexValue = 17;
        declutterPriority = 0.5; // Medium priority for settlements
            break;
          default:
            symbolType = 'city';
            zIndexValue = 18;
        declutterPriority = 0.7; // Standard priority for cities
    }

    return {
      id: `symbol-${type}`,
      name: `${type}s`,
          type: 'symbol',
          symbol: symbolType,
      showLabels,
          labelColumn: 'name_en',
          zIndex: zIndexValue,
          opacity: 1,
          scale: scaleValue,
          scaleType: 'fixed',
          scaleColumn: '',
          visible: 1,
          showInLegend: false,
      declutter: true,
      declutterPriority, // Add declutter priority to layer config
          style: {
            strokeWidth: 1.4,
            stroke: type === 'epicentre' ? '#CC0000' : 'var(--color-text-primary)',
            fill: type === 'epicentre' ? '#CC0000' : 'var(--color-background-paper)',
            labelStyle: {
              fontWeight: 'bold',
              fontSize: 'var(--font-size-md)',
              showHalo: true,
              color: { r: 0, g: 0, b: 0, a: 1 },
          fontFamily: 'var(--font-family-base)',
          declutterOrder: 1 / declutterPriority, // Higher numbers = lower priority
          padding: [3, 3, 3, 3], // Add padding around text
          overflow: true // Allow text to overflow its render box
            }
          },
          data: {
            type: 'FeatureCollection',
            features: features
          }
        };
  }, []);

  // Separate database locations from analysis locations for better persistence
  const { dbLocations, analysisLocations } = useMemo(() => {
    if (!locations) return { dbLocations: [], analysisLocations: [] };
    
    const db = [];
    const analysis = [];
    
    locations.forEach(location => {
      if (location.properties?.source === 'db') {
        db.push(location);
      } else {
        analysis.push(location);
      }
    });
    
    return { dbLocations: db, analysisLocations: analysis };
  }, [locations]);

  // Memoize the grouped locations - NEVER clear database locations during loading
  const groupedLocations = useMemo(() => {
    const grouped = {};
    
    // Always include database locations (these should never disappear)
    dbLocations.forEach(location => {
      const type = location.properties.type || 'city';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(location);
    });
    
    // Add analysis locations only if not loading (these can change with pillars)
    if (!locationsLoading) {
      analysisLocations.forEach(location => {
        const type = location.properties.type || 'city';
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push(location);
      });
    }
    
    return grouped;
  }, [dbLocations, analysisLocations, locationsLoading]);

  // Memoize non-symbol layers and symbol layers
  const nonSymbolLayers = useMemo(() => {
    return layers?.filter(layer => layer.type !== 'symbol') || [];
  }, [layers]);

  const symbolLayers = useMemo(() => {
    return layers?.filter(layer => layer.type === 'symbol') || [];
  }, [layers]);
        
  // Update layers when locations change - but don't skip updates for database locations
  useEffect(() => {
    if (!map) return;

    // Create new symbol layers from grouped locations
    const newSymbolLayers = Object.entries(groupedLocations).map(([type, features]) => 
      createSymbolLayer(type, features)
    );

    // Always update layers with database locations, even during analysis loading
    const updatedLayers = [...nonSymbolLayers, ...newSymbolLayers];
    
    // Compare layer IDs first (faster than deep comparison)
    const currentLayerIds = layers.map(l => l.id).sort().join(',');
    const newLayerIds = updatedLayers.map(l => l.id).sort().join(',');

    if (currentLayerIds !== newLayerIds) {
      throttledUpdateLayers(updatedLayers);
    }
  }, [
    map,
    groupedLocations, // This now includes persistent database locations
    nonSymbolLayers,
    createSymbolLayer,
    throttledUpdateLayers,
    layers
  ]); // Removed locationsLoading dependency to prevent skipping updates

  // Use the mapOptions prop from parent instead of fetching independently
  useEffect(() => {
    if (initialMapOptions) {
      setMapOptions(initialMapOptions);
    }
  }, [initialMapOptions]);

  useEffect(() => {
    if (!initialMapOptions) return;
    
    // Ensure center coordinates are in the correct format
    const center = Array.isArray(initialMapOptions.center) && initialMapOptions.center.length === 2
      ? initialMapOptions.center
      : [0, 0];
      
    // Only update non-zoom options
    setMapOptions(prev => ({
      ...prev,
      ...initialMapOptions,
      center,
      zoom: prev?.zoom || initialMapOptions?.zoom
    }));
  }, [JSON.stringify(initialMapOptions)]);

  const renderAdmin1Layer = () => {
    if (!map || !admin1GeometryData) return null;
    
    return (
      <Admin1Layer
        ref={admin1LayerRef}
        map={map}
        geoJsonData={admin1GeometryData}
        mapAnalysisData={mapAnalysisData}
        selectedPillarKey={selectedPillarKey}
        mapAnalysisDataType={typeof mapAnalysisData}
      />
    );
  };

  // Add annotations hook
  const { annotations, loading: annotationsLoading, error: annotationsError, vectorLayer: annotationsLayer } = 
    useMapAnnotations(viewId, map, fontVersion);

  // Update layers when annotations layer changes
  useEffect(() => {
    if (!annotationsLayer || !map) return;

    // Only update if layers have actually changed
    const updatedLayers = [...nonSymbolLayers, ...symbolLayers];
    
    // Add annotations layer if it exists and isn't already in the layers
    const existingAnnotationsLayer = layers?.find(layer => layer?.id === 'annotations');
    if (!updatedLayers.some(layer => layer.id === 'annotations')) {
      updatedLayers.push({
        id: 'annotations',
        name: 'Annotations',
        type: 'vector',
        layer: annotationsLayer,
        zIndex: 5000, // Increased from 1000 to 5000 to ensure it's above all symbol layers
        visible: true
      });
    }

    const currentLayerIds = (layers || []).map(l => l.id).sort().join(',');
    const newLayerIds = updatedLayers.map(l => l.id).sort().join(',');
    const annotationsLayerChanged = !!existingAnnotationsLayer && existingAnnotationsLayer.layer !== annotationsLayer;

    if (currentLayerIds !== newLayerIds || annotationsLayerChanged) {
      throttledUpdateLayers(updatedLayers);
    }
  }, [annotationsLayer, map, nonSymbolLayers, symbolLayers, layers, throttledUpdateLayers]);

  // Generate dynamic map source text
  const mapDataSourceText = useMemo(() => {
    const sources = new Set();

    // Custom Layers Sources
    if (customLayers) {
      customLayers.forEach(layer => {
        // Use metadata source/attribution if available, otherwise fallback to name
        // checking specifically for is_visible property although hook filters it, 
        // to be safe in case hook changes or we use all layers later
        if (layer.is_visible !== false) {
           const source = layer.metadata?.source || layer.metadata?.attribution || layer.name;
           if (source) sources.add(source);
        }
      });
    }

    if (sources.size === 0) {
      return '';
    }

    return `Source: ${Array.from(sources).join(', ')}`;
  }, [customLayers]);

  const mapBasemapText = 'Basemap: OpenStreetMap, Esri World Hillshade';

  return (
    <div 
      className="map-container" 
      style={{ height: '100%', position: 'relative' }}
      onMouseLeave={handleMapContainerMouseLeave}
    >
      <OpenLayersMap
        ref={ref}
        setMap={setMap}
        map={map}
                    zoom={2}  // Use minimal zoom - extent fitting will determine actual zoom
        minZoom={mapOptions?.minZoom || 1}  // Reasonable bounds for extent-based fitting
        maxZoom={mapOptions?.maxZoom || 20}
        center={initialCenter}
        mapSettings={mapSettings}
        enableDragPan={true}
        showScale={true}
        scaleBar={true}
        scaleBarPosition="bottomRight"
        adm1Data={adm1Data}
        view={view}
      >
        <div id="bottomRight" ref={bottomRightRef} style={{ paddingBottom: `${paddingBottom}px` }} className={styles.mapBottomRight}>
          <div className="overview" />
          <div className="scale" />
        </div>
        <div id="topRight" ref={topRightRef} className={`${styles.mapTopRight} ${!eventsPanelCollapsed ? styles.eventsOpen : ''}`}>
          <div className={`scale ${!eventsPanelCollapsed ? styles.eventsOpen : ''}`} />
          <div className={`overview ${!eventsPanelCollapsed ? styles.eventsOpen : ''}`} />
        </div>
        {/* Tilemap Mini View - positioned below overview map */}
        {tilemapAvailable && (
          <div style={{
            position: 'absolute',
            top: '165px',
            right: !eventsPanelCollapsed ? '15px' : '38px',
            width: '112px',
            height: '112px',
            backgroundColor: 'white',
            border: '1px solid #FAFAFA',
            borderRadius: '0px',
            overflow: 'hidden',
            zIndex: 112,
          }}>
            <TilemapVisualization
              viewId={viewId}
              mapAnalysisData={mapAnalysisData}
              hoveredSeverity={hoveredSeverity}
              hoveredPcode={hoveredPcode}
              isMouseOverLegend={isMouseOverLegend}
              onTileHover={(pcode, data) => {
                if (pcode) {
                  setHoveredPcode(pcode);
                } else {
                  setHoveredPcode(null);
                }
              }}
              mapUpdatedAt={mapUpdatedAt}
              width={112}
              height={112}
              compact={true}
            />
          </div>
        )}
        <div id="topLeft" ref={topLeftRef} className={styles.mapTopLeft}>
          <div className="scale" />
          <div className="overview" />
        </div>
        <div id="bottomLeft" ref={bottomLeftRef} style={{ paddingBottom: `${paddingBottom - 10}px` }} className={styles.mapBottomLeft}>
          <div className="overview" style={{ paddingBottom: 2 }} />
          <div className="scale" style={{ marginTop: 3, marginBottom: 2 }} />
        </div>
        
        {/* Show disclaimer in map when header is hidden */}
        {!showHeader && selectedView && (
          <div style={{
            position: 'absolute',
            bottom: '27px',
            left: '14px',
            zIndex: 1001,
            cursor: 'pointer'
          }}>
            <Disclaimer 
              selectedView={selectedView}
              variant="map"
              sx={{
                fontSize: 'var(--font-size-sm)',
                minWidth: 'auto',
                whiteSpace: 'nowrap'
              }}
            />
          </div>
        )}
        
        {/* Custom Layers */}
        {map && customLayers && customLayers.map(layer => (
          <CustomLayer 
            key={`custom-layer-${layer.id}`}
            map={map}
            layerConfig={layer}
          />
        ))}

        <LayerManager
          map={map}
          layers={layers}
          mapOptions={mapOptions}
          mapSettings={mapSettings}
          selectedPillarKey={selectedPillarKey}
          symbolLayerZIndex={1500}
          fontVersion={fontVersion}
        />
        
        {/* Add Admin1 Layer for displaying province boundaries */}
        {map && admin1GeometryData && (
          <>
            <Admin1Layer
              map={map}
              geoJsonData={admin1GeometryData}
              mapAnalysisData={mapAnalysisData}
              showLabels={false}
              zIndex={0}
              opacity={mapAnalysisData && mapAnalysisData.content ? 0.2 : 0}
              hoveredSeverity={hoveredSeverity}
              hoveredPcode={hoveredPcode}
              onHoverPcode={setHoveredPcode}
              isMouseOverLegend={isMouseOverLegend}
              selectedPillarKey={selectedPillarKey}
              mapUpdatedAt={mapUpdatedAt}
              hideTooltipRef={hideTooltipRef}
            />
          </>
        )}
      </OpenLayersMap>
      
      {/* Display map analysis title and legend */}
      {(displayableMapInfo || isSuperAdmin) && (
        <div style={{ position: 'absolute', top: '87px', left: '13px', zIndex: 1000, pointerEvents: 'none', maxWidth: '250px' }}>
          <div>
            {displayableMapInfo?.title && (
              <div className="map-subtitle">
                {displayableMapInfo.title}
              </div>
            )}

            {(formattedLastUpdated || isSuperAdmin) && (
              <>
                <div
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    if (hideTooltipRef.current) {
                      hideTooltipRef.current();
                    }
                  }}
                  onMouseMove={(e) => {
                    e.stopPropagation();
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    fontSize: 'var(--font-size-sm)',
                    opacity: 0.85,
                    marginTop: '6px',
                    marginBottom: '8px',
                    backgroundColor: 'rgb(240, 240, 240)',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    width: 'fit-content',
                    marginLeft: '2px',
                    pointerEvents: 'auto'
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handlePreviousMapAnalysis}
                    disabled={currentHistoryIndex >= mapAnalysisHistory.length - 1 || !loadMapAnalysisById}
                    sx={{ p: 0.25, opacity: currentHistoryIndex >= mapAnalysisHistory.length - 1 ? 0.3 : 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  </IconButton>

                  <div
                    onClick={handleHistoryMenuOpen}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: (mapAnalysisHistory.length > 1 || isSuperAdmin) ? 'pointer' : 'default'
                    }}
                  >
                    {isSchedulerProcessing ? (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        sx={{ color: 'text.secondary', opacity: 0.7 }}
                      />
                    ) : (
                      <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary', opacity: 0.7 }} />
                    )}
                    <span style={{ fontStyle: 'italic', color: 'rgba(0,0,0,0.7)' }}>{formattedLastUpdated || (mapAnalysisLoading ? 'Loading…' : 'No map analysis')}</span>
                    {(mapAnalysisHistory.length > 1 || isSuperAdmin) && (
                      <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.secondary', opacity: 0.5 }} />
                    )}
                  </div>

                  <IconButton
                    size="small"
                    onClick={handleNextMapAnalysis}
                    disabled={currentHistoryIndex <= 0 || !loadMapAnalysisById}
                    sx={{ p: 0.25, opacity: currentHistoryIndex <= 0 ? 0.3 : 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  </IconButton>
                </div>

                <Menu
                  anchorEl={historyMenuAnchor}
                  open={Boolean(historyMenuAnchor)}
                  onClose={handleHistoryMenuClose}
                  PaperProps={{
                    sx: {
                      maxHeight: 400,
                      width: isSuperAdmin ? 320 : 240,
                      mt: 0.5
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 12px' }}>
                    <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.7rem', p: 0, minHeight: 'auto' }}>
                      Map Analysis History ({mapAnalysisHistory.length})
                    </MenuItem>
                    {isSuperAdmin && (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton 
                          size="small" 
                          onClick={handleEditPillar}
                          sx={{ 
                            p: 0.25, 
                            '& svg': { fontSize: '1rem' },
                            display: 'flex',
                            gap: 0.5,
                            fontSize: '0.7rem',
                            '& span': { color: 'text.secondary' }
                          }}
                          color="primary"
                        >
                          <TerminalIcon />
                          <span>Edit Prompts</span>
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={handleRegenerateMapAnalysis}
                          sx={{ 
                            p: 0.25, 
                            '& svg': { fontSize: '1rem' },
                            display: 'flex',
                            gap: 0.5,
                            fontSize: '0.7rem',
                            '& span': { color: 'text.secondary' }
                          }}
                          color="primary"
                        >
                          <AutoModeIcon />
                          <span>Generate New</span>
                        </IconButton>
                      </div>
                    )}
                  </div>
                  <Divider />
                  {mapAnalysisHistory.length === 0 && (
                    <MenuItem disabled sx={{ opacity: 0.8, fontStyle: 'italic' }}>
                      No map analysis yet
                    </MenuItem>
                  )}
                  {mapAnalysisHistory.map((item) => {
                    const itemDate = new Date(item.completed_at || item.updated_at || item.created_at);
                    const isCurrentItem = item.id === mapAnalysisData?.id;

                    return (
                      <MenuItem
                        key={item.id}
                        onClick={() => handleSelectHistoryItem(item.id)}
                        selected={isCurrentItem}
                        sx={{
                          py: 0.95,
                          px: 1.5,
                          minHeight: 'unset',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Typography variant="body2" sx={{ fontWeight: isCurrentItem ? 600 : 400, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                            {format(itemDate, 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem', lineHeight: 1.2 }}>
                            {format(itemDate, 'h:mm a')} • {formatDistanceToNow(itemDate, { addSuffix: true }).replace('about ', '')}
                          </Typography>
                        </div>

                        {isSuperAdmin && (
                          <div style={{ display: 'flex', gap: '2px', marginLeft: '6px' }}>
                            <Tooltip title="View in Admin">
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleViewMapAnalysisInAdmin(e, item.id)}
                                sx={{ p: 0.375, '& svg': { fontSize: '1rem' } }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={(e) => handleDeleteMapAnalysisClick(e, item.id)}
                                sx={{ p: 0.375, '& svg': { fontSize: '1rem' } }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </>
            )}
            {displayableMapInfo?.description && (
              <div style={{
                fontSize: 'var(--font-size-md)',
                color: 'var(--color-fg-default)',
                marginBottom: '8px',
                lineHeight: 'var(--line-height-normal)',
                padding: '3px 8px', 
                backgroundColor: 'rgba(255,255,255,0.85)',
                whiteSpace: 'normal'
              }}>
                {displayableMapInfo.description.replace(/\[\d+\]/g, '').trim()}
              </div>
            )}
            
            {/* Enhanced Info Sections */}
            {(displayableMapInfo?.methodology || displayableMapInfo?.mainFindings || displayableMapInfo?.limitations) && (
              <div style={{
                marginTop: '8px',
                marginBottom: '12px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '4px',
                padding: '8px',
                pointerEvents: 'auto',
                maxHeight: '300px',
                overflowY: 'auto',
                scrollbarWidth: 'none', /* Firefox */
                msOverflowStyle: 'none', /* Internet Explorer 10+ */
                WebkitScrollbar: { display: 'none' } /* WebKit */
              }}>
                {/* Methodology Section */}
                {displayableMapInfo?.methodology && (
                  <div style={{ marginBottom: '8px' }}>
                    <button
                      onClick={() => toggleSection('methodology')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 0',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'bold',
                        color: 'var(--color-fg-default)',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minWidth: '140px',
                        textAlign: 'left'
                      }}
                    >
                      <MethodologyIcon sx={{ fontSize: '16px', marginRight: '6px', color: 'var(--color-fg-default)' }} />
                      Methodology
                      <span style={{ marginLeft: '6px' }}>
                        {expandedSections.methodology ? '▼' : '▶'}
                      </span>
                    </button>
                    {expandedSections.methodology && (
                      <div style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-fg-muted)',
                        lineHeight: 'var(--line-height-normal)',
                        paddingLeft: '16px',
                        marginTop: '4px'
                      }}>
                        {displayableMapInfo.methodology}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Main Findings Section */}
                {displayableMapInfo?.mainFindings && (
                  <div style={{ marginBottom: '8px' }}>
                    <button
                      onClick={() => toggleSection('findings')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 0',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'bold',
                        color: 'var(--color-fg-default)',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minWidth: '140px',
                        textAlign: 'left'
                      }}
                    >
                      <FindingsIcon sx={{ fontSize: '16px', marginRight: '6px', color: 'var(--color-fg-default)' }} />
                      Key Findings
                      <span style={{ marginLeft: '6px' }}>
                        {expandedSections.findings ? '▼' : '▶'}
                      </span>
                    </button>
                    {expandedSections.findings && (
                      <div style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-fg-muted)',
                        lineHeight: 'var(--line-height-normal)',
                        paddingLeft: '16px',
                        marginTop: '4px'
                      }}>
                        {displayableMapInfo.mainFindings}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Limitations Section */}
                {displayableMapInfo?.limitations && (
                  <div style={{ marginBottom: '8px' }}>
                    <button
                      onClick={() => toggleSection('limitations')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 0',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'bold',
                        color: 'var(--color-fg-default)',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minWidth: '140px',
                        textAlign: 'left'
                      }}
                    >
                      <LimitationsIcon sx={{ fontSize: '16px', marginRight: '6px', color: 'var(--color-fg-default)' }} />
                      Limitations
                      <span style={{ marginLeft: '6px' }}>
                        {expandedSections.limitations ? '▼' : '▶'}
                      </span>
                    </button>
                    {expandedSections.limitations && (
                      <div style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-fg-muted)',
                        lineHeight: 'var(--line-height-normal)',
                        paddingLeft: '16px',
                        marginTop: '4px'
                      }}>
                        {displayableMapInfo.limitations}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {displayableMapInfo?.legend && (
            <div>
          <div 
            className="map-legend" 
            style={{ 
              padding: '5px 8px', 
              borderRadius: '0px',
              fontSize: 'var(--font-size-md)',
              width: 'fit-content',
              maxWidth: '250px',
              backgroundColor: 'rgba(255,255,255,0.85)',
              pointerEvents: 'auto',
            }}
            onMouseEnter={handleLegendMouseEnter}
            onMouseLeave={() => setIsMouseOverLegend(false)}
          >
            <div className="map-legend-title">
              {displayableMapInfo.legend?.title || 'Legend'}
            </div>

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '0px',
              width: 'fit-content',
              minWidth: 'fit-content',
              pointerEvents: 'auto'
            }}>
              {Array.isArray(displayableMapInfo.legend) ? (
                displayableMapInfo.legend.map((item, index) => (
                  <LegendItem
                    key={index}
                    item={item}
                    hoveredSeverity={hoveredSeverity}
                    setHoveredSeverity={setHoveredSeverity}
                  />
                ))
              ) : null}
            </div>
            

          </div>
<div style={{
  backgroundColor: 'rgba(255,255,255,0.85)',

}}>
<LegendTooltip 

hoveredSeverity={hoveredSeverity}
mapLegend={displayableMapInfo.legend}
/>
</div>
</div>
          )}
        </div>
      )}
      
      <div className="map-source">
        {mapDataSourceText && <div>{mapDataSourceText}</div>}
        <div>{mapBasemapText}</div>
      </div>

      {/* Add loading indicator for annotations */}
      {annotationsLoading && (
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 'var(--font-size-sm)'
        }}>
          Loading annotations...
        </div>
      )}
      
      {/* Add error message for annotations */}
      {annotationsError && (
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          color: 'red',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 'var(--font-size-sm)'
        }}>
          Error loading annotations
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteMapAnalysisCancel}
        aria-labelledby="delete-map-dialog-title"
        aria-describedby="delete-map-dialog-description"
      >
        <DialogTitle id="delete-map-dialog-title">
          Delete Map Analysis Record?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-map-dialog-description">
            Are you sure you want to delete this map analysis record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteMapAnalysisCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteMapAnalysisConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleRegenerateCancel}
        aria-labelledby="regenerate-map-dialog-title"
        aria-describedby="regenerate-map-dialog-description"
      >
        <DialogTitle id="regenerate-map-dialog-title">
          Regenerate Map Analysis?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="regenerate-map-dialog-description">
            This will start a background process to generate a new map analysis for this view and pillar. The process may take a minute to complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegenerateCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRegenerateConfirm} color="primary" variant="contained" autoFocus>
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}), (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Compare all props except for arrays and objects that need deep comparison
  
  // Check simple props first
  const simplePropsEqual = (
    prevProps.showLegend === nextProps.showLegend &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.error === nextProps.error &&
    prevProps.selectedPillarKey === nextProps.selectedPillarKey &&
    prevProps.eventsPanelCollapsed === nextProps.eventsPanelCollapsed &&
    prevProps.paddingBottom === nextProps.paddingBottom &&
    prevProps.selectedPillarId === nextProps.selectedPillarId &&
    prevProps.showHeader === nextProps.showHeader &&
    prevProps.view?.iso3 === nextProps.view?.iso3 &&
    prevProps.selectedView?.id === nextProps.selectedView?.id &&
    prevProps.selectedSubPillar?.id === nextProps.selectedSubPillar?.id &&
    prevProps.onMapAnalysisData === nextProps.onMapAnalysisData &&
    prevProps.onUpdateLayers === nextProps.onUpdateLayers &&
    prevProps.setMap === nextProps.setMap
  );
  
  if (!simplePropsEqual) {
    return false;
  }
  
  // Deep compare adm1Data array
  const adm1DataEqual = isEqual(prevProps.adm1Data, nextProps.adm1Data);
  if (!adm1DataEqual) {
    return false;
  }
  
  // Deep compare layers array
  const layersEqual = isEqual(prevProps.layers, nextProps.layers);
  if (!layersEqual) {
    return false;
  }
  
  // Compare mapOptions and mapSettings objects
  const mapOptionsEqual = isEqual(prevProps.mapOptions, nextProps.mapOptions);
  if (!mapOptionsEqual) {
    return false;
  }
  
  const mapSettingsEqual = isEqual(prevProps.mapSettings, nextProps.mapSettings);
  if (!mapSettingsEqual) {
    return false;
  }
  
  return true;
});

Map.displayName = 'Map';

export default Map;
